import { useEffect, useState } from "react";
import { DetailsIconsOptions, IconMoreVerticalDots } from "../../../../../components/buttons/IconButtons";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import { useDispatch, useSelector } from "react-redux";
import TableFunction from "../../../../../components/table/TableFunction";
import { permissionObj } from "../../../../../Helpers/permission";
import { useTranslation } from "react-i18next";
import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE, filterByName } from "../../../../../Apis/functions";
import { GetGeofenceList } from "../../../../../reduxToolkit/FineGeofence/FineGeofenceApi";
import { resetGetGeofenceList } from "../../../../../reduxToolkit/FineGeofence/FineGeofenceSlice";
import { GEOFENCES_MODEL } from "../../Manage/Model/Model";

/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */

/**
 * GeofencesTabApp
 *
 * Hook that encapsulates all logic and state management for the Geofences Tab component.
 * Handles fetching geofences, filtering by search term, modal toggling, selection, and map centering.
 *
 * @param {Object} props - Component props
 * @param {string} props.searchElement - Search term for filtering geofences
 * @param {Function} props.setSearchElement - Setter to update search term
 * @param {Function} props.setTotalElements - Setter to update total number of geofences
 * @param {Function} props.handleMapMove - Function to update the map center
 *
 * @returns {Object} Object containing handlers and state used in the Geofences Tab
 * @property {Function} getActionsButtons - Generates action buttons for each geofence row
 * @property {Function} handleToggleModal - Toggles the geofence details modal
 * @property {boolean} showModal - Indicates if the details modal is open
 * @property {Array} geofences - Array of geofences displayed in the table
 * @property {string|number|null} selected - ID of the currently selected geofence
 * @property {Function} setSelected - Setter to update the selected geofence
 * @property {Array|null} center - Coordinates of the map center
 */
const GeofencesTabApp = ({ searchElement, setSearchElement, setTotalElements, handleMapMove }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { getGeofenceList } = useSelector((state) => state?.FineGeofenceSlice);

    /** State */
    const [selectedList, setSelectedList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modified, setModified] = useState(false);
    const [geofences, setGeofences] = useState([]);
    const [selected, setSelected] = useState(null);
    const [center, setCenter] = useState(null);

    /** Toggle modal visibility */
    const handleToggleModal = () => setShowModal(!showModal);

    /** Table helper for selected rows */
    const { handleMenuToggle, handleClose, openMenu } = TableFunction({
        selectedList,
        setSelectedList,
    });

    /**
     * Fetch geofences data from API, reset search, and set map center
     */
    const getData = () => {
        dispatch(resetGetGeofenceList());
        setSearchElement("");
        dispatch(GetGeofenceList()).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                setTotalElements(data.length);
                if (data.length > 0) {
                    let aux = GEOFENCES_MODEL.buildGeofenceListMap(data);
                    setGeofences(aux);
                    let center = GEOFENCES_MODEL.getGeofenceCenter(aux[0]);
                    if (center) {
                        setCenter(center);
                        handleMapMove(center[0], center[1]);
                    }
                } else {
                    handleMapMove(DEFAULT_LATITUDE, DEFAULT_LONGITUDE);
                }
            }
        });
    };

    /**
     * Generate action buttons for each geofence row
     * @param {Object} item - Geofence item
     * @param {number} index - Index of the row
     * @returns {JSX.Element} Rendered dropdown action buttons
     */
    const getActionsButtons = (item, index) => {
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.geofenceId}`}
                icon={<IconMoreVerticalDots />}
            >
                {(DEV || permission?.includes(permissionObj?.WEB_TRAFFIC_DASHBOARD_GEOFENCES_READ)) &&
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t("details")}
                    icon={<DetailsIconsOptions />}
                    click={() => {
                        setSelected(item.geofenceId);
                        handleToggleModal();
                    }}
                />
                }
            </DropDownMenuProfile>
        );
    };

    /**
     * Filter geofences whenever search term or API list changes
     */
    useEffect(() => {
        if (getGeofenceList.length === 0) return;
        if (searchElement === "") {
            setGeofences(GEOFENCES_MODEL.buildGeofenceListMap(getGeofenceList));
        } else {
            const filteredOptions = filterByName(geofences, searchElement);
            setGeofences(filteredOptions);
        }
    }, [searchElement, getGeofenceList]);

    /** Initial fetch on mount */
    useEffect(() => {
        getData();
    }, []);

    /** Refetch data when modified */
    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    return {
        getActionsButtons,
        handleToggleModal,
        showModal,
        geofences,
        selected,
        setSelected,
        center
    };
};

export default GeofencesTabApp;
